import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import BioAgeClockCard from "../shared/BioAgeClockCard";
import { useConsultation } from "../../ConsultationContext";

/**
 * Shared center column for Section 1 steps 1–4: bio-age clock with the
 * "Age N" subtitle, plus (optionally) the comparison line below.
 *
 * Visual reference: Figma nodes 14711:145940 (clock+age) and the comparison
 * text from 14711:145976 onward.
 */
const BioAgeCenterColumn: React.FC<{
  /** Render the comparison line (reserves its space so the clock doesn't
   *  shift when it appears). */
  showComparison?: boolean;
  /** Fade the comparison line in/out without affecting layout. */
  comparisonVisible?: boolean;
}> = ({ showComparison = false, comparisonVisible = true }) => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();
  const { biological, chronological } = data.bioAge;

  const delta =
    biological !== null && chronological !== null
      ? chronological - biological
      : null;

  return (
    <Box sx={styles.col}>
      <BioAgeClockCard
        size={160}
        showSubtitle={!!chronological}
        subtitleOverride={chronological ? `Age ${chronological}` : null}
      />
      {showComparison && delta !== null && chronological !== null && (
        <Box sx={[styles.comparison, styles.comparisonReveal(comparisonVisible)]}>
          {delta > 0 ? (
            <>
              <Box component="span" sx={styles.deltaGood(theme)}>
                {delta} {delta === 1 ? "year" : "years"} younger
              </Box>
              <Box component="span" sx={styles.deltaWeak(theme)}>
                {" "}than your chronological age of {chronological}
              </Box>
            </>
          ) : delta < 0 ? (
            <>
              <Box component="span" sx={styles.deltaBad(theme)}>
                {Math.abs(delta)} {Math.abs(delta) === 1 ? "year" : "years"} older
              </Box>
              <Box component="span" sx={styles.deltaWeak(theme)}>
                {" "}than your chronological age of {chronological}
              </Box>
            </>
          ) : (
            // delta === 0 — bio age matches chronological. Render the
            // "On track" message in primary blue so sub-step 2 still adds
            // something visible below the clock (otherwise it looks
            // identical to sub-step 1) and stays distinct from the
            // younger (green) / older (red) variants.
            <>
              <Box component="span" sx={styles.deltaNeutral(theme)}>
                On track
              </Box>
              <Box component="span" sx={styles.deltaWeak(theme)}>
                {" "}with your chronological age of {chronological}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BioAgeCenterColumn;

const styles = {
  col: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "24px",
  },
  comparison: {
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    textAlign: "center" as const,
  },
  // Fade the comparison line in while keeping its reserved space, so the
  // clock above it never shifts between sub-steps.
  comparisonReveal: (visible: boolean) => ({
    opacity: visible ? 1 : 0,
    transition: "opacity 350ms ease",
  }),
  deltaGood: (theme: Theme) => ({ color: theme.colors_ds.SUCCESS_ICON_MAIN }),
  deltaBad: (theme: Theme) => ({ color: theme.colorTokens.D[60] }),
  // Neutral / on-track variant uses primary blue so it stays distinct from
  // the younger (green) and older (red) deltas.
  deltaNeutral: (theme: Theme) => ({ color: theme.colors_ds.PRIMARY_TEXT_MAIN }),
  deltaWeak: (theme: Theme) => ({ color: theme.colors_ds.NEUTRAL_TEXT_WEAK }),
};
